<template>
  <Transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-[1px]"
      @click.self="closeDrawer"
    />
  </Transition>

  <Transition name="slide">
    <aside
      v-if="modelValue"
      class="fixed right-0 top-0 z-[9999] h-screen w-full max-w-2xl overflow-y-auto bg-white shadow-2xl"
    >
      <div
        class="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-6 py-5 backdrop-blur"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="text-start">
            <p
              class="text-xs font-semibold uppercase tracking-[0.2em] text-[#2f8f2f]"
            >
              Item do relatório
            </p>

            <h2 class="mt-1 text-2xl font-bold text-gray-900">
              {{ isEdit ? "Editar item" : "Novo item" }}
            </h2>

            <p class="mt-1 text-sm text-gray-500">
              Cadastre o ponto observado, recomendação, prioridade e informações
              técnicas.
            </p>
          </div>

          <button
            type="button"
            class="rounded-xl p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            @click="closeDrawer"
          >
            <PhX class="h-5 w-5" />
          </button>
        </div>
      </div>

      <form class="px-6 py-6" @submit.prevent="submitForm">
        <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Tipo do item
            </label>

            <select v-model="form.item_type" class="report-input">
              <option
                v-for="type in itemTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Setor
            </label>

            <input
              v-model="form.sector"
              type="text"
              class="report-input"
              placeholder="Ex: Produção, Expedição..."
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Local
            </label>

            <input
              v-model="form.location"
              type="text"
              class="report-input"
              placeholder="Ex: Linha 01, Área externa..."
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Prioridade
            </label>

            <select v-model="form.priority" class="report-input">
              <option value="">Não definida</option>

              <option
                v-for="priority in priorities"
                :key="priority.value"
                :value="priority.value"
              >
                {{ priority.label }}
              </option>
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Nível de risco
            </label>

            <select v-model="form.risk_level" class="report-input">
              <option value="">Não definido</option>

              <option
                v-for="risk in riskLevels"
                :key="risk.value"
                :value="risk.value"
              >
                {{ risk.label }}
              </option>
            </select>
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Título do ponto observado
            </label>

            <input
              v-model="form.title"
              type="text"
              class="report-input"
              placeholder="Ex: Extintor com acesso obstruído, máquina sem proteção, área sem sinalização..."
            />
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Observação
            </label>

            <textarea
              v-model="form.description"
              rows="4"
              class="report-input resize-none"
              placeholder="Descreva de forma objetiva o que foi identificado durante a visita..."
            />
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Ponto de melhoria
            </label>

            <textarea
              v-model="form.improvement_point"
              rows="4"
              class="report-input resize-none"
              placeholder="Explique qual ponto precisa ser corrigido ou melhorado..."
            />
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Recomendação
            </label>

            <textarea
              v-model="form.recommendation"
              rows="4"
              class="report-input resize-none"
              placeholder="Informe a recomendação técnica para eliminar, reduzir ou controlar o risco identificado..."
            />
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Observação técnica complementar
            </label>

            <textarea
              v-model="form.technical_observation"
              rows="4"
              class="report-input resize-none"
              placeholder="Adicione detalhes técnicos, normas relacionadas ou comentários internos..."
            />
          </div>
        </div>

        <div class="mt-6 rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
          <div class="flex flex-col gap-5 sm:flex-row">
            <div
              class="flex h-44 w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm sm:w-56"
            >
              <img
                v-if="photoPreview"
                :src="photoPreview"
                alt="Prévia da foto do item"
                class="h-full w-full object-cover"
              />

              <div v-else class="text-center">
                <PhImageSquare class="mx-auto h-12 w-12 text-gray-300" />
                <p class="mt-2 text-xs text-gray-400">Sem imagem</p>
              </div>
            </div>

            <div class="flex-1">
              <h3 class="text-sm font-semibold text-gray-800">
                Foto do ponto observado
              </h3>

              <p class="mt-1 text-sm text-gray-500">
                Adicione uma foto do local, equipamento, condição insegura ou
                ponto de melhoria observado na visita.
              </p>

              <div class="mt-4 flex flex-wrap items-center gap-2">
                <label
                  class="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-green-100 bg-white px-4 py-2.5 text-sm font-semibold text-[#2f8f2f] shadow-sm transition hover:bg-green-50"
                >
                  <PhUploadSimple class="h-5 w-5" />
                  Selecionar foto

                  <input
                    type="file"
                    class="hidden"
                    accept="image/*"
                    capture="environment"
                    @change="handlePhotoChange"
                  />
                </label>

                <button
                  v-if="photoPreview"
                  type="button"
                  class="inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                  @click="removePhoto"
                >
                  <PhTrash class="h-4 w-4" />
                  Remover foto
                </button>
              </div>

              <p class="mt-3 text-xs text-gray-400">
                Formatos recomendados: JPG, PNG ou WEBP.
              </p>
            </div>
          </div>
        </div>

        <div
          class="sticky bottom-0 mt-8 flex items-center justify-end gap-3 border-t border-gray-100 bg-white py-5"
        >
          <button
            type="button"
            class="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
            @click="closeDrawer"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="loading"
          >
            <PhCircleNotch v-if="loading" class="h-5 w-5 animate-spin" />
            <PhCheck v-else class="h-5 w-5" />

            {{ isEdit ? "Salvar alterações" : "Adicionar item" }}
          </button>
        </div>
      </form>
    </aside>
  </Transition>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import {
  PhCheck,
  PhCircleNotch,
  PhImageSquare,
  PhTrash,
  PhUploadSimple,
  PhX,
} from "@phosphor-icons/vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "submit", "close"]);

const form = reactive(getDefaultForm());

const selectedPhotoFile = ref(null);
const photoPreview = ref("");

const isEdit = computed(() => Boolean(form.id));

const itemTypes = [
  {
    label: "Foto + observação",
    value: "photo_observation",
  },
  {
    label: "Texto livre",
    value: "free_text",
  },
  {
    label: "Checklist",
    value: "checklist_answer",
  },
  {
    label: "Plano de ação",
    value: "action_plan",
  },
  {
    label: "Seção / título",
    value: "section_title",
  },
  {
    label: "Imagem + texto",
    value: "image_text",
  },
];

const riskLevels = [
  { label: "Baixo", value: "low" },
  { label: "Moderado", value: "medium" },
  { label: "Substancial", value: "substantial" },
  { label: "Grave", value: "serious" },
  { label: "Crítico", value: "critical" },
  { label: "Risco à saúde", value: "health_risk" },
  { label: "Risco legal", value: "legal_risk" },
];

const priorities = [
  { label: "Baixa", value: "low" },
  { label: "Média", value: "medium" },
  { label: "Alta", value: "high" },
  { label: "Urgente", value: "urgent" },
];

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;

    fillForm(props.item);
  }
);

watch(
  () => props.item,
  (item) => {
    if (props.modelValue) {
      fillForm(item);
    }
  }
);

function getDefaultForm() {
  return {
    id: null,
    item_type: "photo_observation",
    sector: "",
    location: "",
    title: "",
    description: "",
    improvement_point: "",
    recommendation: "",
    technical_observation: "",
    risk_level: "",
    priority: "",
    photo_path: "",
    photo_url: "",
    metadata: null,
  };
}

function fillForm(item = null) {
  Object.assign(form, getDefaultForm());

  selectedPhotoFile.value = null;

  if (photoPreview.value && photoPreview.value.startsWith("blob:")) {
    URL.revokeObjectURL(photoPreview.value);
  }

  photoPreview.value = "";

  if (!item) return;

  Object.assign(form, {
    ...getDefaultForm(),
    ...item,
  });

  photoPreview.value = item.photo_url || "";
}

function submitForm() {
  emit("submit", {
    id: form.id,
    isEdit: isEdit.value,
    photoFile: selectedPhotoFile.value,
    photoPreview: photoPreview.value,
    payload: {
      item_type: form.item_type,
      sector: form.sector?.trim(),
      location: form.location?.trim(),
      title: form.title?.trim(),
      description: form.description?.trim(),
      improvement_point: form.improvement_point?.trim(),
      recommendation: form.recommendation?.trim(),
      technical_observation: form.technical_observation?.trim(),
      risk_level: form.risk_level || null,
      priority: form.priority || null,
      photo_path: form.photo_path || null,
      photo_url: form.photo_url || null,
      metadata: form.metadata,
    },
  });
}

function closeDrawer() {
  emit("update:modelValue", false);
  emit("close");
}

function handlePhotoChange(event) {
  const [file] = event.target.files || [];

  if (!file) return;

  selectedPhotoFile.value = file;

  if (photoPreview.value && photoPreview.value.startsWith("blob:")) {
    URL.revokeObjectURL(photoPreview.value);
  }

  photoPreview.value = URL.createObjectURL(file);
}

function removePhoto() {
  if (photoPreview.value && photoPreview.value.startsWith("blob:")) {
    URL.revokeObjectURL(photoPreview.value);
  }

  selectedPhotoFile.value = null;
  photoPreview.value = "";

  form.photo_path = "";
  form.photo_url = "";
}
</script>

<style scoped>
.report-input {
  @apply w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none transition placeholder:text-gray-300 focus:border-[#2f8f2f] focus:ring-2 focus:ring-green-100;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
